/**
 * Frontend helper: compute the union of RAT options that some modem at a
 * location can deliver. Used by the RAT selector UI to disable options
 * with no capable modem at the chosen location.
 *
 * Capability flags live on the `modem_models` collection (curated per-model
 * defaults); per-modem records (`CentralEdgeModem.capabilities`) hold optional
 * overrides. The effective capability is the merge — that's the contract
 * `EdgeModemCard` uses, and we mirror it here so the UI matches the allocator.
 */

import {
  FORCED_RAT_TO_CAPABILITY,
  computeEffectiveCapabilities,
  getCapabilityBooleanValue,
  type ForcedRat,
  type ModemModel,
} from '@simcheck/shared-types';

interface ModemCapabilityCarrier {
  modemModelId?: string;
  capabilities?: Record<string, any>;
}

/**
 * Per-RAT availability summary across a set of locations. Used by the
 * country-first Quick Tests UX so the RAT selector can tell the user
 * "5G — supported at 2 of 4 locations" without having to drill into a
 * specific location first.
 */
export interface RatAvailabilityForCountry {
  /** Locations within the country that have at least one capable online modem. */
  supportedLocationIds: string[];
  /** Total visible locations considered (the denominator for the caption). */
  totalLocations: number;
}

/**
 * Returns the set of RATs (excluding 'auto') that at least one modem in
 * the input list can deliver. 'auto' is always available regardless and
 * is intentionally not in the returned set — callers should treat it as
 * the always-on baseline.
 *
 * `modemModels` is the curated capability source (loaded once via
 * `getAllModemModels`). When a modem references a model id, the model's
 * capabilities are merged with the per-modem overrides.
 */
export function availableRatsAtLocation(
  modems: ModemCapabilityCarrier[],
  modemModels: ModemModel[],
): Set<Exclude<ForcedRat, 'auto'>> {
  const result = new Set<Exclude<ForcedRat, 'auto'>>();
  for (const modem of modems) {
    const model = modem.modemModelId
      ? modemModels.find(m => m.id === modem.modemModelId)
      : null;
    const effective = computeEffectiveCapabilities(
      model?.capabilities,
      modem.capabilities,
    );
    if (!effective) continue;
    for (const rat of Object.keys(FORCED_RAT_TO_CAPABILITY) as Array<Exclude<ForcedRat, 'auto'>>) {
      if (result.has(rat)) continue;
      const flag = FORCED_RAT_TO_CAPABILITY[rat];
      if (getCapabilityBooleanValue(effective, flag, false)) {
        result.add(rat);
      }
    }
  }
  return result;
}

/**
 * Country-scoped union: returns, per RAT, the set of locationIds at which
 * at least one online modem can serve that RAT. The caller can render
 * "(supported at N of M locations)" by comparing `supportedLocationIds.length`
 * to `totalLocations`. RATs not present in the returned map have zero
 * capable modems anywhere in the country and should be disabled in the UI.
 *
 * Inputs:
 *   - `modemsByLocation` — pre-grouped by locationId. Keys MUST be every
 *     visible locationId in the country (even if the value is an empty
 *     array) so `totalLocations` matches the denominator the user expects.
 *   - `modemModels` — curated capability source.
 */
export function availableRatsForCountry(
  modemsByLocation: Map<string, ModemCapabilityCarrier[]>,
  modemModels: ModemModel[],
): Map<Exclude<ForcedRat, 'auto'>, RatAvailabilityForCountry> {
  const totalLocations = modemsByLocation.size;
  const map = new Map<Exclude<ForcedRat, 'auto'>, RatAvailabilityForCountry>();

  for (const [locationId, modems] of modemsByLocation) {
    const ratsHere = availableRatsAtLocation(modems, modemModels);
    for (const rat of ratsHere) {
      const cur = map.get(rat);
      if (cur) {
        cur.supportedLocationIds.push(locationId);
      } else {
        map.set(rat, { supportedLocationIds: [locationId], totalLocations });
      }
    }
  }

  return map;
}

/**
 * The modem-model capability flags the RAT selector + allocator care about.
 * One flag per network generation the platform can force/require.
 */
export const RAT_CAPABILITY_FLAGS = [
  'supports5G',
  'supports4G',
  'supports3G',
  'supports2G',
  'supportsLTEM',
  'supportsNBIoT',
  // Voice capability — not a RAT generation, but the Voice Call Check quick
  // test gates allocation on it, so it must be surfaced in effectiveModemCaps
  // for the gray-out check (someModemSupportsTest reads from this list).
  'supportsVoLTE',
] as const;

/**
 * Per-modem effective capability booleans for the RAT-relevant flags, one
 * record per modem in the input list. Mirrors `availableRatsAtLocation` but
 * keeps each modem separate so callers can ask "does a SINGLE modem support
 * both X and Y" — which is exactly what the allocator requires (a modem must
 * satisfy the forced RAT AND the flow's capability set; the per-RAT union
 * would hide the case where two different modems each cover only one half).
 *
 * Modems whose capabilities can't be resolved (no model + no overrides) are
 * omitted, matching the allocator's fail-closed capability gate.
 */
export function effectiveModemCaps(
  modems: ModemCapabilityCarrier[],
  modemModels: ModemModel[],
): Array<Record<string, boolean>> {
  const out: Array<Record<string, boolean>> = [];
  for (const modem of modems) {
    const model = modem.modemModelId
      ? modemModels.find(m => m.id === modem.modemModelId)
      : null;
    const effective = computeEffectiveCapabilities(model?.capabilities, modem.capabilities);
    if (!effective) continue;
    const rec: Record<string, boolean> = {};
    for (const flag of RAT_CAPABILITY_FLAGS) {
      rec[flag] = getCapabilityBooleanValue(effective, flag, false);
    }
    out.push(rec);
  }
  return out;
}

/**
 * Returns true if at least one modem (from `effectiveModemCaps`) can satisfy
 * BOTH the forced RAT and the test's required-capability set. Mirrors the
 * allocator's `findFreeModem` capability gate exactly:
 *   - `forcedRat === 'auto'` → the RAT half is a no-op.
 *   - `requiredAnyOf` is ANY-of (modem needs at least one of the flags); an
 *     empty list means the test has no capability requirement.
 * Used to gray out Quick Tests that have no viable modem under the chosen
 * network type, so the user avoids the "none of the modems can be both" error.
 */
export function someModemSupportsTest(
  modemCaps: Array<Record<string, boolean>>,
  forcedRat: ForcedRat,
  requiredAnyOf: string[],
): boolean {
  const ratFlag: string | null =
    forcedRat && forcedRat !== 'auto'
      ? FORCED_RAT_TO_CAPABILITY[forcedRat as Exclude<ForcedRat, 'auto'>]
      : null;
  return modemCaps.some(caps => {
    if (ratFlag && !caps[ratFlag]) return false;
    if (requiredAnyOf.length > 0 && !requiredAnyOf.some(flag => caps[flag])) return false;
    return true;
  });
}
